import { set } from '@ember/object';

import { clearAttribute } from 'beebee-base/lib/utility';
import { paesiMondoAlfa3, paesiMondoAlfa2 } from 'beebee-base/lib/list_taxes';

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 *      keep in sync with the code):
 *   role:      lib — the shared "can this contact be invoiced?" gate: required-field
 *              validation of the buyer, plus the placeholder fiscal identifiers a
 *              non-Italian contact needs before an invoice can be issued.
 *   exports:   checkRequiredFiscalFields(contact, options), completaStraniero(contact)
 *   callers:   controllers/checkout.js and controllers/payment-request.js, each
 *              through its own thin checkRequired/completaStraniero method
 *   tests:     checkout-getters-test.js, payment-request-tasks-test.js
 *   gotchas:   both functions WRITE on the contact through `set()` — the `*Error`
 *              flags are rendered by Glimmer, and a native assignment would not
 *              dirty the tag. completaStraniero must never run for an Italian
 *              contact: it would overwrite real postal/fiscal data with placeholders.
 *
 * checkout.js and payment-request.js carried byte-identical copies of this logic —
 * they differed only in how they resolve the contact (the wizard's `contact` getter
 * vs a direct peek of the logged user), so it lives here once and each controller
 * passes its own record in.
 */

/**
 * Validates the buyer contact and, for a foreign address, completes the fiscal
 * placeholders. Writes the `*Error` flags the forms render.
 *
 * @param {object} contact the contact record to validate (mutated)
 * @param {object} [options]
 * @param {object} [options.notRequired] tenant `ecommerce_not_required_field` map
 * @param {boolean} [options.showError] when false, validate without flagging the UI
 * @returns {boolean} true when every required field is present
 */
export function checkRequiredFiscalFields(
  contact,
  { notRequired: notRequiredOption, showError = true } = {}
) {
  const fieldReset = [
    'nameError',
    'ragSocialeError',
    'birthError',
    'luogoError',
    'mobileError',
    'mailError',
    'cfError',
    'codfiscaleError',
    'addressError',
  ];
  const fieldTrim = [
    'ragione_sociale',
    'first_name',
    'last_name',
    'cod_fiscale',
    'luogo_nascita',
    'mail',
    'mobile',
  ];

  // Reset error fields if showError is true
  if (showError) {
    fieldReset.forEach((field) => {
      set(contact, field, false);
    });
  }

  // Trim fields
  fieldTrim.forEach((field) => {
    const value = clearAttribute(contact.get(field));
    set(contact, field, value);
  });

  let checkField = true;
  const notRequired = notRequiredOption || {};
  const isFisicaGiuridica = contact.get('fisica_giuridica');
  const address = contact.get('addresses')[0];
  const country = address?.location_address_country;
  const countryCode = address?.location_address_country_code;

  // Check for 'ragione sociale' or name fields
  if (isFisicaGiuridica) {
    if (!contact.get('ragione_sociale')) {
      if (showError) {
        set(contact, 'ragSocialeError', true);
      }
      checkField = false;
    }
  } else {
    if (!contact.get('first_name') || !contact.get('last_name')) {
      if (showError) {
        set(contact, 'nameError', true);
      }
      checkField = false;
    }
  }

  // Check birth and place of birth fields
  if (!contact.get('data_nascita') && !notRequired.data_nascita) {
    if (showError) {
      set(contact, 'birthError', true);
    }
    checkField = false;
  }
  if (!contact.get('luogo_nascita') && !notRequired.luogo_nascita) {
    if (showError) {
      set(contact, 'luogoError', true);
    }
    checkField = false;
  }

  // Check email and mobile
  if (!contact.get('mail')) {
    if (showError) {
      set(contact, 'mailError', true);
    }
    checkField = false;
  }
  if (!contact.get('mobile') && !notRequired.mobile) {
    if (showError) {
      set(contact, 'mobileError', true);
    }
    checkField = false;
  }

  // Check address fields
  if (
    !(
      address?.location_address_street &&
      address?.location_address_house &&
      address?.location_address_postal_code &&
      address?.location_address_city &&
      address?.location_address_country
    ) &&
    !notRequired.address
  ) {
    if (showError) {
      set(contact, 'addressError', true);
    }
    checkField = false;
  }

  // Check Codice Fiscale for Italian addresses
  const isItaly = country === 'Italia' || ['IT', 'ITA'].includes(countryCode);
  if (isItaly) {
    // completaStraniero() must never run for Italian contacts: it would
    // overwrite the real postal code / fiscal data with placeholder values.
    if (
      !notRequired.cod_fiscale &&
      (!contact.get('cod_fiscale') || contact.get('codFiscaleValidMsg'))
    ) {
      if (showError) {
        set(contact, 'cfError', true);
      }
      checkField = false;
    }
  } else {
    completaStraniero(contact);
  }

  return checkField;
}

/**
 * Fills the placeholder fiscal identifiers a non-Italian contact needs for the
 * electronic invoice (an `XXXXXXX` recipient code and a country-prefixed
 * CF/PIVA built on the contact id). No-op for an Italian or address-less contact.
 *
 * @param {object} item the contact record to complete (mutated)
 */
export function completaStraniero(item) {
  if (!item) return;
  const contactAddress = item.addresses?.[0];
  if (!contactAddress) return;
  const countryCode = contactAddress.location_address_country_code;
  const country = contactAddress.location_address_country;
  const isItaly = country === 'Italia' || ['IT', 'ITA'].includes(countryCode);
  const contactId = item.id || '';

  if (isItaly || !countryCode) {
    return;
  }

  const convertCountryCodeToAlpha2 = (countryCode) => {
    const index = paesiMondoAlfa3.indexOf(countryCode);

    if (index === -1) {
      return countryCode.substring(0, 2);
    }

    return paesiMondoAlfa2[index];
  };

  const alpha2Code = convertCountryCodeToAlpha2(countryCode);

  set(contactAddress, 'location_address_postal_code', '00000');
  set(item, 'codice_destinatario', 'XXXXXXX');

  if (item.fisica_giuridica) {
    const rimanentiCF = 14 - contactId.length;
    const contactCF = `${alpha2Code}${'00000000000000'.substring(0, rimanentiCF)}${contactId}`;
    set(item, 'cod_fiscale', contactCF);
  } else {
    set(item, 'cod_fiscale', null);
  }

  const rimanentiPIVA = 11 - contactId.length;
  const pIVA = `${alpha2Code}${'999999999'.substring(0, rimanentiPIVA)}${contactId}`;
  set(item, 'piva', pIVA);
}
